// v16 kopyası (Overview v16 gri widget düzeni). Canlı BudgetChannelsCard'dan farkı:
// hedefsiz satırda kendiliğinden odaklanan input yok ("Set target" düğmesi), boş çubuk
// yerine kanal adı, Toplam satırı sade. Canlı kart bu dosyadan etkilenmez.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Pencil, Wallet, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const API_BASE = 'https://api.glowytics.com';

// ── Demo brand: fabricated budget-usage data ────────────────────────────────
// The "Glowytics Demo Store" has no real ad connections or targets, so the
// pacing/connections endpoints return nothing. Render believable dummy numbers
// instead. MTD spend is derived from how far into the month we are × a per-channel
// pace multiplier (some channels over-pace, some under) so the card looks live and
// the progress bars show varied colours regardless of the day it's viewed.
const DEMO_BRAND_ID = 'demo-brand-glowytics-2024';

// A brand is "demo" if it's the canonical demo brand, has DEMO status, or its name
// contains "demo" (covers the various *Demo Store* test brands). Keyed off the
// cached selectedBrand object so it works regardless of which demo brand is active.
function isDemoBrand(brandId) {
  if (brandId === DEMO_BRAND_ID) return true;
  try {
    const b = JSON.parse(localStorage.getItem('selectedBrand') || 'null');
    if (b && b.id === brandId) {
      if (String(b.status || '').toUpperCase() === 'DEMO') return true;
      if (/demo/i.test(b.name || '')) return true;
    }
  } catch (_) {}
  return false;
}
const DEMO_CHANNELS = [
  { channel_key: 'GOOGLE_ADS',    target_spend: 250000, pace: 1.06 },
  { channel_key: 'META_ADS',      target_spend: 180000, pace: 0.98 },
  { channel_key: 'TIKTOK_ADS',    target_spend: 90000,  pace: 0.88 },
  { channel_key: 'PINTEREST_ADS', target_spend: 45000,  pace: 1.14 },
  { channel_key: 'CRITEO_ADS',    target_spend: 60000,  pace: 0.95 },
  { channel_key: 'RTB_HOUSE',     target_spend: 40000,  pace: 1.02 },
];

function buildDemoBudget() {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysPassed = now.getDate();
  const daysRemaining = Math.max(daysInMonth - daysPassed, 0);
  const monthFrac = daysPassed / daysInMonth;

  const channels = DEMO_CHANNELS.map((c) => {
    const mtd_spend = Math.round(c.target_spend * monthFrac * c.pace);
    const remaining_spend = Math.max(c.target_spend - mtd_spend, 0);
    const daily_required_spend = daysRemaining > 0 ? Math.round(remaining_spend / daysRemaining) : 0;
    return {
      channel_key: c.channel_key,
      label: c.channel_key,
      mtd_spend,
      target_spend: c.target_spend,
      remaining_spend,
      daily_required_spend,
    };
  });

  const sum = (f) => channels.reduce((a, c) => a + (Number(c[f]) || 0), 0);
  const totals = {
    mtd_spend: sum('mtd_spend'),
    target_spend: sum('target_spend'),
    remaining_spend: sum('remaining_spend'),
    daily_required_spend: sum('daily_required_spend'),
  };

  return { channels, totals, days_in_month: daysInMonth, days_passed: daysPassed };
}

// channel_key (from pacing API) → display metadata
const CHANNEL_META = {
  GOOGLE_ADS:    { label: 'Google Ads',    logo: '/google_ads-512.webp' },
  META_ADS:      { label: 'Meta Ads',      logo: '/6033716.png' },
  TIKTOK_ADS:    { label: 'TikTok Ads',    logo: '/tik-tokiconss.png' },
  PINTEREST_ADS: { label: 'Pinterest',     logo: '/pinterestlogopng.png' },
  RTB_HOUSE:     { label: 'RTB House',     logo: '/RTBhouseiconPNG.png' },
  CRITEO_ADS:    { label: 'Criteo',        logo: '/CRcriteopng.png' },
  ADJUST:        { label: 'Adjust',        logo: '/adjustlogo.png' },
};

const fmtTRY = (val) => `₺${Math.round(Number(val || 0)).toLocaleString('tr-TR')}`;
const fmtCompactTRY = (val) => {
  const n = Math.round(Number(val || 0));
  if (n >= 1_000_000) return `₺${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `₺${(n / 1_000).toFixed(0)}k`;
  return `₺${n.toLocaleString('tr-TR')}`;
};

// ── Responsive columns, driven by the CARD's own width (not the viewport) ───
// This card is embedded in a narrow right-hand column on Overview2 while the
// viewport itself is wide, so viewport breakpoints (`2xl:`) switched the extra
// columns on inside a container that could not fit them and the row overflowed
// to the right. Measure the card instead and pick the column set from that.
const COL_W = { logo: 28, spent: 62, target: 78, daily: 62, left: 62 };
const COL_GAP = 10;
const BAR_MIN = 64; // bar + % badge

function colsFor(width) {
  const fixed = (...w) => w.reduce((a, b) => a + b, 0);
  const fits = (extra) =>
    24 /* px-3 padding */ +
    fixed(COL_W.logo, COL_W.spent, COL_W.target, ...extra) +
    COL_GAP * (3 + extra.length) +
    BAR_MIN <= width;

  if (fits([COL_W.daily, COL_W.left])) return { daily: true, left: true };
  if (fits([COL_W.daily])) return { daily: true, left: false };
  return { daily: false, left: false };
}

function gridStyleFor(cols) {
  const parts = [`${COL_W.logo}px`, 'minmax(0,1fr)', `${COL_W.spent}px`, `${COL_W.target}px`];
  if (cols.daily) parts.push(`${COL_W.daily}px`);
  if (cols.left) parts.push(`${COL_W.left}px`);
  return { display: 'grid', gridTemplateColumns: parts.join(' '), columnGap: `${COL_GAP}px` };
}

function ringColorFor(hasTarget, spentPct) {
  if (!hasTarget) return '#9ca3af';
  if (spentPct > 80) return '#ef4444';
  if (spentPct > 50) return '#f59e0b';
  return '#3b82f6';
}

async function saveTarget({ channelKey, year, month, spend_target }) {
  const brandId = localStorage.getItem('currentBrandId');
  if (!brandId) throw new Error('No brand selected');
  const res = await fetch(`${API_BASE}/api/monthly-targets`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
    body: JSON.stringify({
      year,
      month,
      channel_key: channelKey,
      spend_target,
      revenue_source: 'ga4',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Save failed');
  return data;
}

function ChannelRow({ channel, onSaved, cols }) {
  const meta = CHANNEL_META[channel.channel_key] || { label: channel.label, logo: null };
  const hasTarget = channel.target_spend != null && Number(channel.target_spend) > 0;
  const spentPct = hasTarget
    ? Math.min(Math.round((Number(channel.mtd_spend || 0) / Number(channel.target_spend)) * 100), 100)
    : 0;
  const barColor = ringColorFor(hasTarget, spentPct);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const onBlur = async (e) => {
    if (e.target.dataset.cancelled === '1') { setEditing(false); return; }
    const value = e.target.value.replace(/[^0-9]/g, '');
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0 && numValue !== Number(channel.target_spend)) {
      try {
        setSaving(true);
        const now = new Date();
        await saveTarget({
          channelKey: channel.channel_key,
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          spend_target: numValue,
        });
        toast.success(`${meta.label} target saved`);
        onSaved && onSaved();
      } catch (err) {
        toast.error(err.message || 'Failed to save target');
      } finally {
        setSaving(false);
      }
    }
    setEditing(false);
  };

  return (
    <div
      className="items-center px-3 py-2.5 hover:bg-gray-50/60 transition-colors"
      style={gridStyleFor(cols)}
    >
      {/* Logo only — name removed to save horizontal space */}
      <div className="flex items-center justify-center min-w-0" title={meta.label}>
        {meta.logo ? (
          <img src={meta.logo} alt={meta.label} className="w-6 h-6 object-contain rounded" />
        ) : (
          <div className="w-6 h-6 rounded bg-gray-100" />
        )}
      </div>

      <div className="min-w-0 flex items-center gap-2">
        {hasTarget ? (
          <div className="flex-1 h-2 bg-white rounded-full overflow-hidden min-w-[24px]">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${spentPct}%`, backgroundColor: barColor }}
            />
          </div>
        ) : (
          <span className="flex-1 text-xs font-medium text-gray-700 truncate">{meta.label}</span>
        )}
        <span className="text-xs font-bold tabular-nums w-9 text-right flex-shrink-0" style={{ color: barColor }}>
          {hasTarget ? `${spentPct}%` : '—'}
        </span>
      </div>

      <div className="min-w-0 text-right">
        <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Spent</p>
        <p className="text-xs font-bold tabular-nums text-gray-900 mt-0.5 truncate" title={fmtTRY(channel.mtd_spend)}>
          {fmtCompactTRY(channel.mtd_spend)}
        </p>
      </div>

      <div className="min-w-0 text-right">
        <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Target</p>
        {editing ? (
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <span className="text-xs font-semibold text-gray-700 flex-shrink-0">₺</span>
            <input
              type="text"
              className="w-full min-w-0 border border-blue-300 rounded px-1 py-0.5 text-xs font-semibold tabular-nums text-right focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-500 placeholder-gray-400"
              defaultValue={hasTarget ? Number(channel.target_spend).toLocaleString('en-US') : ''}
              placeholder="—"
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
                else if (e.key === 'Escape') { e.target.dataset.cancelled = '1'; setEditing(false); }
              }}
              onBlur={onBlur}
              autoFocus
            />
          </div>
        ) : !hasTarget ? (
          <button
            type="button"
            className="mt-0.5 w-full text-right text-xs font-semibold text-blue-600 hover:text-blue-700"
            onClick={() => setEditing(true)}
          >
            Set target
          </button>
        ) : (
          <button
            type="button"
            className="mt-0.5 w-full cursor-pointer hover:text-blue-600 transition-colors inline-flex items-center justify-end gap-1 text-xs font-bold tabular-nums text-gray-900"
            onClick={() => setEditing(true)}
            title={`Target ${fmtTRY(channel.target_spend)} — click to edit`}
          >
            <span className="truncate">{fmtCompactTRY(channel.target_spend)}</span>
            <Pencil className="w-3 h-3 opacity-40 flex-shrink-0" />
          </button>
        )}
      </div>

      {/* Daily + Remaining — shown only when the CARD itself is wide enough */}
      {cols.daily && (
        <div className="min-w-0 text-right">
          <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Daily</p>
          <p className="text-xs font-bold tabular-nums text-gray-700 mt-0.5 truncate" title={hasTarget ? fmtTRY(channel.daily_required_spend) : ''}>
            {hasTarget ? fmtCompactTRY(channel.daily_required_spend) : '—'}
          </p>
        </div>
      )}

      {cols.left && (
        <div className="min-w-0 text-right">
          <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Left</p>
          <p className="text-xs font-bold tabular-nums text-gray-700 mt-0.5 truncate" title={hasTarget ? fmtTRY(channel.remaining_spend) : ''}>
            {hasTarget ? fmtCompactTRY(channel.remaining_spend) : '—'}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * BudgetChannelsCard — self-contained card showing per-channel MTD spend vs
 * monthly target for every ACTIVE ApiConnection. Reused on `/budget-overview`
 * (full page) and embedded on Overview2 in place of the daily perf table.
 *
 * Props:
 *  - title (string)           : card header label, default "Budget Usage"
 *  - showHeader (bool)        : render the card-internal title row + refresh, default true
 *  - showTotalSummary (bool)  : render the aggregate-totals bar at the top, default false
 *  - className (string)       : wrapper class override
 */
export default function BudgetChannelsCard({
  title = 'Budget Usage',
  showHeader = true,
  showTotalSummary = false,
  className = '',
}) {
  const [data, setData] = useState(null);
  const [activeConnections, setActiveConnections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Own width, not the viewport's — see colsFor().
  const wrapRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      setCardWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setCardWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  const cols = useMemo(() => colsFor(cardWidth), [cardWidth]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { setData(null); setActiveConnections(new Set()); return; }
      if (isDemoBrand(brandId)) {
        setData(buildDemoBudget());
        setActiveConnections(new Set(DEMO_CHANNELS.map((c) => c.channel_key)));
        return;
      }
      const headers = { 'X-Brand-ID': brandId };
      const [pacingRes, connRes] = await Promise.all([
        fetch(`${API_BASE}/api/pacing/overview`, { credentials: 'include', headers }),
        fetch(`${API_BASE}/api/auth/connections`, { credentials: 'include', headers }),
      ]);
      if (!pacingRes.ok) throw new Error(`HTTP ${pacingRes.status}`);
      setData(await pacingRes.json());

      const active = new Set();
      if (connRes.ok) {
        const connJson = await connRes.json();
        const upper = (s) => String(s || '').toUpperCase();
        if (Array.isArray(connJson)) {
          for (const c of connJson) {
            if (upper(c.status) === 'ACTIVE') active.add(upper(c.platform));
          }
        } else if (connJson && typeof connJson === 'object') {
          for (const [k, v] of Object.entries(connJson)) {
            if (v && upper(v.status) === 'ACTIVE') active.add(upper(k));
          }
        }
      }
      setActiveConnections(active);
    } catch (e) {
      console.warn('[BudgetChannelsCard] load failed:', e.message);
      setError(e.message || 'Failed to load');
      setData(null);
      setActiveConnections(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('storage', handler);
    window.addEventListener('brandChanged', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('brandChanged', handler);
    };
  }, [load]);

  const channels = useMemo(() => {
    if (!data?.channels) return [];
    if (!activeConnections) return [];
    return data.channels.filter((c) => {
      if (c.channel_key === 'ADJUST') return false;
      return activeConnections.has(c.channel_key);
    });
  }, [data, activeConnections]);

  const totals = data?.totals;
  const totalHasTarget = totals?.target_spend != null && Number(totals.target_spend) > 0;
  const totalPct = totalHasTarget
    ? Math.min(Math.round((Number(totals.mtd_spend || 0) / Number(totals.target_spend)) * 100), 100)
    : 0;
  const totalRingColor = ringColorFor(totalHasTarget, totalPct);
  const remainingDays = data ? (Number(data.days_in_month || 0) - Number(data.days_passed || 0)) : 0;

  return (
    <div ref={wrapRef} className={`bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
              <Wallet className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
              <p className="text-[11px] text-gray-500 truncate">Monthly target vs spent · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/budget-overview"
              className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-gray-500 hover:text-blue-600 transition-colors"
              title="Open Budget Overview"
            >
              <span className="hidden sm:inline">View budgets</span>
              <span className="sm:hidden">Budgets</span>
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-gray-50 ring-1 ring-gray-200 text-gray-700 px-2.5 py-1.5 rounded-lg disabled:opacity-50"
              title="Refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
          </div>
        </div>
      )}

      {showTotalSummary && totals && (
        <div
          className="items-center px-3 py-2.5 border-b border-gray-200/70"
          style={gridStyleFor(cols)}
        >
          {/* Σ badge sits in the logo column so every value column lines up with the rows */}
          <div className="flex items-center justify-center min-w-0" title="All channels">
            <div className="w-6 h-6 rounded bg-slate-200/70 flex items-center justify-center text-[11px] font-bold text-slate-600">Σ</div>
          </div>

          {/* Label + bar + % — value columns below start at the same x as the rows' */}
          <div className="min-w-0 flex items-center gap-2">
            <span className="flex-1 text-xs font-semibold text-gray-900 truncate" title="All channels">
              Total
            </span>
            {/* The label wins over the bar on narrow cards — the % badge already carries the number */}
            {cols.left && totalHasTarget && (
              <div className="flex-1 h-2 bg-white rounded-full overflow-hidden min-w-[24px]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${totalPct}%`, backgroundColor: totalRingColor }}
                />
              </div>
            )}
            <span className="text-xs font-bold tabular-nums w-9 text-right flex-shrink-0" style={{ color: totalRingColor }}>
              {totalHasTarget ? `${totalPct}%` : '—'}
            </span>
          </div>

          <div className="min-w-0 text-right">
            <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Spent</p>
            <p className="text-xs font-bold tabular-nums text-gray-900 mt-0.5 truncate" title={fmtTRY(totals.mtd_spend)}>{fmtCompactTRY(totals.mtd_spend)}</p>
          </div>

          <div className="min-w-0 text-right">
            <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Target</p>
            <p className="text-xs font-bold tabular-nums text-gray-900 mt-0.5 truncate" title={totalHasTarget ? fmtTRY(totals.target_spend) : ''}>
              {totalHasTarget ? fmtCompactTRY(totals.target_spend) : <span className="text-gray-400">—</span>}
            </p>
          </div>

          {cols.daily && (
            <div className="min-w-0 text-right">
              <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Daily</p>
              <p className="text-xs font-bold tabular-nums text-gray-900 mt-0.5 truncate" title={fmtTRY(totals.daily_required_spend)}>{fmtCompactTRY(totals.daily_required_spend)}</p>
            </div>
          )}

          {cols.left && (
            <div className="min-w-0 text-right">
              <p className="text-[9px] uppercase font-medium text-gray-400 tracking-wide leading-none">Left</p>
              <p className="text-xs font-bold tabular-nums text-gray-900 mt-0.5 truncate" title={fmtTRY(totals.remaining_spend)}>{fmtCompactTRY(totals.remaining_spend)}</p>
              <p className="text-[9px] text-gray-500 leading-none">{remainingDays}d</p>
            </div>
          )}
        </div>
      )}

      {loading && !data ? (
        <div className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : channels.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-500">
          No connected ad platforms or targets for this brand.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {channels.map((c) => (
            <ChannelRow key={c.channel_key} channel={c} onSaved={load} cols={cols} />
          ))}
        </div>
      )}

      {error && (
        <div className="px-4 py-2 text-xs text-red-600 border-t border-red-100 bg-red-50/50">{error}</div>
      )}
    </div>
  );
}
